import {
  Component,
  Event,
  type EventEmitter,
  h,
  Prop,
  State,
  Listen,
  Element,
} from '@stencil/core';
import {GenericCardTitle} from '../../generic-subcomponents/generic-card-title/generic-card-title';
import {GenericCardLabels} from '../../generic-subcomponents/generic-card-labels/generic-card-labels';
import {ProductCardCommStatus} from './subcomponents/product-card-comm-status/product-card-comm-status';
import {GenericCardDescription} from '../../generic-subcomponents/generic-card-description/generic-card-description';
import {type SearchResultClickEvent} from '../../../search-tab/SearchResultClickEvent';
import {GenericCardEventsLayer} from '../../generic-subcomponents/generic-card-event-layer/generic-card-event-layer';
import type {SearchApiDocument, WCEventData} from '../../../../../api/model/response/documents/SearchResult';
import type {SearchProduct} from '../../../../../api/model/response/documents/public/Product';
import {makeLogger} from '../../../../../logger/logger';
import {isMobile} from '../../../../../utils/media';
import type {ProductDocumentsData} from '../../../../../api/model/response/documents/public/ProductDocuments';
import {defaultLabelsList} from '../../utils/defaultLabelsList';
import {Strategies} from '../../../../strategies/Strategies';
import type {ProductRangesData} from '../../../../../api/model/response/documents/public/ProductsRanges';
import type {EcommerceSe, TransformedRange} from './types/newProductCard.type';
import {getGAEventData, getRangesCombinedTerm, getWcEventData} from './helpers/helpers';
import {
  GenericCardDropdownBody
} from '../../generic-subcomponents/generic-card-dropdown-body/generic-card-dropdown-body';
import {
  ProductDropdownBodyContent
} from './subcomponents/product-card-dropdown-body-content/product-card-dropdown-body-content';
import {
  GenericCardCtaBottomSection
} from '../../generic-subcomponents/generic-card-cta-bottom-section/generic-card-cta-bottom-section';
import {ECommerceSection} from './subcomponents/e-commerce-section/e-commerce-section';
import {searchOverflowToggler} from '../../../../../utils/helpers/overflowToggler';
import {ProductCardContainer} from './subcomponents/product-card-container/product-card-container';
import {EolResult} from '../../../../../constants/eolResult';
import {ProduktnetSection} from './subcomponents/produktnet-section/produktnet-section';
import { MadeInAmericaBadge } from './subcomponents/made-in-america-badge/made-in-america-badge';
import type { labelProviderInterface } from '../../../../LabelProviderInterface';
import {ucOptions, type UcItem} from '../../utils/ucOptions';
import {truncateTextForScreenWidth} from '../../../../../utils/helpers/trim-by-screen-width';
import {MarketingProductBadge} from './subcomponents/marketing-product-badge/marketing-product-badge';
import {ProductStockAvailability} from './subcomponents/product-stock-availability/product-stock-availability';

const {info} = makeLogger({
  pathToCurrentFile:
    'components/result-page/new-result-cards/uiaas-search-result-product.tsx',
});

@Component({
  tag: 'uiaas-search-result-product',
  shadow: true,
  styleUrl: 'uiaas-search-result-product.scss',
})
export class ResultCardProductV3 {
  get isOutOfCard(): boolean {
      return this._isOutOfCard;
  }

  set isOutOfCard(value: boolean) {
      this._isOutOfCard = value;
  }

  get isVisible(): boolean {
      return this._isVisible;
  }

  set isVisible(value: boolean) {
      this._isVisible = value;
  }

  @Prop() readonly doc: SearchProduct;
  @Prop() readonly gridView: boolean;
  @Prop() readonly isDashboardView: boolean = false;
  @Prop() readonly isDashboardListVariant: boolean = false;
  @Prop() readonly eShop?: boolean = false;
  @Prop() readonly srt: boolean = false;
  @Prop({mutable: true}) labelProvider?: labelProviderInterface;
  @State() private _isVisible = false;
  @State() mobileView: boolean = isMobile();
  @State() private _isOutOfCard = false;
  @Prop() readonly rtl?: boolean;
  @Prop() readonly isSubstitutionTool?: boolean;
  @Prop() readonly eolProductsNumber?: number;
  @Prop() readonly wcEventData?: WCEventData;
  @Prop() readonly isUnifiedCheckout?: boolean;
  @Prop() readonly isCarousel?: boolean = false;

  @Element() el;

  /**
   * Position of search result in a list
   * (required for CTR-related analysis)
   */
  @Prop() readonly index: number = null;
  @Prop() readonly country: string;
  @Prop() readonly language: string;
  @Prop() readonly brand: string;
  @Prop() readonly titleImportance: string = '';
  @Prop() readonly lazyLoadImages:boolean = false;
  @Prop({mutable: true}) productDocuments: [] | Array<ProductDocumentsData> =
    [];
  @Prop({mutable: true}) productRanges: [] | Array<ProductRangesData> = [];
  @Prop() readonly marketingProductBadgesFilters?: Array<string> = [];

  private handleLeave() {
      this._isOutOfCard = true;
      this._isVisible = false;
  }

  @State() isDropdownOpen = false;
  @State() accessibilityMode;
  @State() iscircularEl = false;
  @State() marketingBadgeSlotsNumber = 0;

  @Prop() readonly strategies: Strategies;

  @Listen('resize', {target: 'window'})
  handleResize() {
      this.mobileView = isMobile();
  }

  @Listen('uiaas:accessibility', {target: 'window'})
  handleAccessibility(ev) {
      this.accessibilityMode = ev.detail;
  }

  @Listen('productsCardDetails', {capture: true})
  productsDetailsEventHandler(event) {
      const detail = event.detail;

      if (!this.productDocuments?.length || !this.productDocuments)
        this.productDocuments = detail?.documents?.documents?.map(
          (item) => item[`${this.language}_${this.country.toUpperCase()}`]
        );

      if (!this.productRanges?.length || !this.productRanges)
        this.productRanges = detail?.ranges?.documents?.map((item) => ({
          openUrl: item.openUrl,
          title: item.title,
        }));
  }

  @Listen('mouseleave', {capture: true})
  handleMouseLeave() {
      if (!this.mobileView && this._isVisible) this.handleLeave();
  }

  @Listen('focusout', {capture: true})
  handleFocusLeave() {
      if (this.mobileView && this._isVisible) this.handleLeave();
  }

  private circularEl? = null;
  private maxBadgesNumber: number = 2;
  private ctaLimit = 2;
  private rangesTerm = '';
  private toggleDropdown = (e) => {
      e?.preventDefault();

      this._isVisible = !this._isVisible;
      this.mobileView && this._isVisible
        ? searchOverflowToggler(true)
        : searchOverflowToggler(false);
  };

  /*
      The following code was used due to the requirement to adjust the dropdown height based on the content.
      Dynamic properties do not allow adding a scroll bar when the calculated height is greater than the available one.
      For this reason, style needs to be calculated from parent size.
      !To remove in phase #2 when ranges and documents will have own tabs in dropdown
    */
  private setMaxHeight = (
    el: any,//eslint-disable-line @typescript-eslint/no-explicit-any
    isGrid: boolean,
    sku: string,
    rangesTerm: string
  ) => {
      const isTermAvailablee = sku || rangesTerm;

      if (isGrid && !this.mobileView && isTermAvailablee) {
        const headerHeight = 46;
        const cardHeight = el.shadowRoot
          .querySelector('.result-card__container')
          .getBoundingClientRect().height;

        el.shadowRoot.querySelector(
          '.generic-card-dropdown-body-content'
        ).style.maxHeight = cardHeight - headerHeight + 'px';
      }
  };

  connectedCallback() {
      const htmlElement = document.querySelector('html');
      this.accessibilityMode =
      htmlElement.classList.contains('se2-accessibility');
  }

  componentWillLoad() {
      if (!this.labelProvider) {
        this.labelProvider = (name, fallback) => {
            info('LabelProvider not provided in ResultCardProductV3 component', [
              name,
              fallback,
            ]);
            return fallback;
        };
      }
  }

  componentDidUpdate() {
      this._isOutOfCard = false;
  }

  componentDidRender() {
      const searchApiDocument = this.doc as unknown as SearchApiDocument;

      this.setMaxHeight(
        this.el,
        this.gridView,
        searchApiDocument?.sku,
        this.rangesTerm
      );

      this.iscircularEl = this.circularEl?.shadowRoot?.querySelectorAll('qds-tag').length > 0;
  }

  @Event({
    eventName: 'search:ResultCardClick',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    resultCardClickEventEmitter: EventEmitter<SearchResultClickEvent>;

  private getCircularNatureToDisplay(doc: SearchApiDocument): string {
      const circularNature = doc?.circularNature ?? '';
      return circularNature ? circularNature + this.labelProvider('additional-conditions.banner.label', ' available') : '';
  }

  @Event({
    eventName: 'ADD_TO_MY_CART',
    bubbles: true,
  })
    addToCartClicked: EventEmitter<Object>;

  @Event({
    eventName: 'ADD_TO_MY_LIST',
    bubbles: true,
  })
    addToMyListClicked: EventEmitter<Object>;

  private sendWCEvent = (eventEmmiter: EventEmitter<Object>, wcEventName: string) => {
      const wcEventData = getWcEventData(wcEventName, this.country, this.language, this.doc.id);
      eventEmmiter.emit(wcEventData);
  };

  private addToCardClickHandler = () => {
      this.sendWCEvent(this.addToCartClicked, ucOptions.ADD_TO_MY_CART);
  };

  private addToMyListClickHandler = () => {
      this.sendWCEvent(this.addToMyListClicked, ucOptions.ADD_TO_MY_LIST);
  };

  private addToMyList = (): HTMLElement => (
    this.isUnifiedCheckout && this.doc?.isCTO == 'false' && <qds-button
      onClick={this.addToMyListClickHandler}
      importance='subdued'
      size='large'
      icon-name='star'
      icon-library="default"
      text={this.wcEventData?.List || this.labelProvider('product.card.add-to-my-list', 'Add to My List')}
      data-card-area={ucOptions.ADD_TO_MY_LIST}
      href={'javascript: void(0)'}
      class='product-card__add-to-my-list'
    />
  );

  private getTruncatedText = (text: string) => {
      // grid view
      if(this.gridView) {
        return truncateTextForScreenWidth(text, 'xsmall');
      }
      // list view
      return truncateTextForScreenWidth(text, 'large');
  };

  render() {
      const circularNatureToDisplay: string = this.getCircularNatureToDisplay(this.doc as unknown as SearchApiDocument);
      // doc.sku value can contain html tag with 'highlight' class
      const sku = (this.doc as unknown as SearchApiDocument)?.sku;
      // doc.skuId value is only ID value without any html tags
      const skuId = (this.doc as unknown as SearchApiDocument)?.id;
      const category = this.doc?.f2Symbol ?? '';
      const ranges = (this.doc as unknown as SearchApiDocument)?.range;
      const supportPage = `https://se.com/${this.country}/${this.language}/work/support/customer-care/contact-schneider-electric.jsp`;

      this.rangesTerm = getRangesCombinedTerm(ranges, category);

      const isButtonVisible = Boolean(sku) || Boolean(this.rangesTerm);
      const dropdownHeader = this.gridView ? this.labelProvider('products.dropdown.documents-and-ranges', 'Documents & ranges') : this.doc.title;
      const price = (this.doc as unknown as SearchApiDocument)?.eshopPrice;
      const priceCurrency = (this.doc as unknown as SearchApiDocument)?.eshopCurrency;
      const priceWithCurrency = `${price} ${priceCurrency}`;
      const isMadeInAmericaBadge = this.doc?.isMadeInAmerica && category === 'Product'; // Ensure it is visible only for products

      // Possible availibility values: 'in_stock', 'out_of_stock'
      const availibility = (this.doc as unknown as SearchApiDocument)?.eshopAvailability;

      const eCommerceData = {
        price: priceWithCurrency,
        supportPage,
        availibility,
        eShopUrl: (this.doc as unknown as SearchApiDocument)?.eshopRedirectUrl,
      };

      const brandStrategy = this.strategies.strategyData?.searchers?.params?.brand || '';
      const disableViewDetailsButton = this.strategies.strategyData?.cardType?.params?.disableViewDetailsButton;
      const showStockAvailability = this.strategies?.strategyData?.cardType?.params?.showStockAvailability;
      const outOfStock = showStockAvailability && !this.doc.stockAvailability;
      const isCompare = this.strategies.strategyData?.cardType?.params?.isCompareFeature && ! outOfStock;
      const isEcommerceSection = (this.eShop && eCommerceData.availibility && eCommerceData.eShopUrl) || this.doc?.produktnetUrl;
      const isProductDropdownSection = (sku || this.rangesTerm) && brandStrategy != 'myschneider';
      const addToMyCartText = this.getTruncatedText(this.wcEventData?.Cart || this.labelProvider('product.card.add-to-my-cart', 'Add to My Cart'));
      const isPromotedWidget = this.doc?.isPromotedWidget;
      const commStatusLabelNumber = ProductCardCommStatus.commStatusNumber || 0;
      const circularLabelNumber = this.iscircularEl ? 1 : 0;
      this.marketingBadgeSlotsNumber = Math.max(this.maxBadgesNumber - commStatusLabelNumber - circularLabelNumber, 0);
      // Display the view details button if the CTA buttons count is less than ctaLimit
      // Condition for CTO: CTO it means Configurational Only,
      // the CTA btns ('Add to Cart' and 'Add to My List') should be displayed only if the product is not CTO (doc?.isCTO == 'false')
      const isViewDetailsBtn = disableViewDetailsButton ? false : [
        (!!isEcommerceSection),
        (isProductDropdownSection),
        (this.isUnifiedCheckout && this.doc?.isCTO == 'false')].filter(c => c == true).length < this.ctaLimit;
      if (this.mobileView && this.isDashboardView) this.doc.description = '';
      return (
        <GenericCardEventsLayer
          clickEmitter={this.resultCardClickEventEmitter}
          doc={this.doc as unknown as SearchProduct}
          index={this.index}
          section={this.isDashboardView ? 'products' : undefined}
          isPromotedWidget={isPromotedWidget}
        >
          <ProductCardContainer
            gridView={this.gridView}
            isDashboardView={this.isDashboardView}
            doc={this.doc}
            srt={this.srt}
            labelProvider={(e, f) => this.labelProvider(e, f)}
            isDropdownVisible={this._isVisible}
            sku={sku}
            rangesTerm={this.rangesTerm}
            strategies={this.strategies}
            rtl={this.rtl}
            isDashboardListVariant={this.isDashboardListVariant}
            isCarousel={this.isCarousel}
          >
            <div class="result-card__image-container">
              <section class={'result-card__thumbnail-section'} data-card-area='image'>
                <uiaas-thumbnail custom-lazy-loading={this.lazyLoadImages} src={this.doc.thumbnailUrl} />
                <MadeInAmericaBadge
                  custom-lazy-loading={this.lazyLoadImages}
                  isActive={isMadeInAmericaBadge}
                  isGridView={this.gridView}
                />
              </section>
            </div>
            <div
              class={{
                'result-card__overlay': true,
                'result-card__overlay--active':
                this._isOutOfCard ||
                ((this.gridView ||
                    this.isDashboardView ||
                    this.mobileView) &&
                  this._isVisible),
              }}
            />
            <div class={'result-card__sections'}>
              {this.isSubstitutionTool && this.eolProductsNumber && this.doc.productEolReplacementStatus === EolResult.SUBSTITUTE ? <qds-tag color='green' status='success' size='small' text={this.labelProvider('result.product.recommended-replacement', 'Recommended Replacement')} class='result-card__eol-tag' /> : ''}
              <section class={'result-card__text-content-section'}>
                <div class='product-card__labels'>
                  <circular-label ref={el=>this.circularEl = el} text={circularNatureToDisplay} class={{'product-card__circular-label': !this.iscircularEl}} />

                  <ProductCardCommStatus
                    commStatus={(this.doc as unknown as SearchProduct).commStatus}
                    labelProvider={(e, f) => this.labelProvider(e, f)}
                    isAlternativeDivider={isButtonVisible}
                    isGridView={this.gridView}
                    isDashboardView={this.isDashboardView}
                    commercializationDate={(this.doc as unknown as SearchProduct).commercializationDate}
                    endOfCommercializationDate={(this.doc as unknown as SearchProduct).endOfCommercializationDate}
                    endOfServiceDate={(this.doc as unknown as SearchProduct).endOfServiceDate}
                    rtl={this.rtl}
                    language={this.language}
                    country={this.country}
                  />

                  <MarketingProductBadge
                    badgeTypes={this.doc?.badgeTypes || []}
                    labelProvider={(e, f) => this.labelProvider(e, f)}
                    slotsNumber={this.marketingBadgeSlotsNumber}
                    marketingProductBadgesFilters={this.marketingProductBadgesFilters}
                  />
                </div>
                <div class={'product-card-header'}>
                  <GenericCardTitle
                    isDropdownVisible={this._isVisible}
                    url={this.doc.url}
                    text={this.doc.title}
                    strategies={this.strategies}
                    displayLongDescription={this.doc.f2Symbol === 'Product'}
                    importance={this.titleImportance}
                  />
                  {!this.mobileView && isCompare ? (
                    <uiaas-checkbox
                      labelProvider={(e, f) => this.labelProvider(e, f)}
                      isGridLike={this.gridView || this.isDashboardView}
                      skuId={skuId}
                      brand="se"
                      country={this.country}
                      language={this.language}
                      index={this.index}
                      isDashboardView={this.isDashboardView}
                    />
                  ) : ''}
                </div>
                <GenericCardLabels
                  labelProvider={(e, f) => this.labelProvider(e, f)}
                  labels={defaultLabelsList(
                    this.doc,
                    this.accessibilityMode
                  )}
                />
                {this.doc.f2Symbol !== 'Product'
                  ? <GenericCardDescription description={(this.doc as unknown as SearchApiDocument).description}/>
                  : ''}
                {showStockAvailability && <ProductStockAvailability labelProvider={this.labelProvider} rtl={this.rtl} stockAvailability={this.doc.stockAvailability} />}
                {!this.mobileView && !outOfStock ? this.addToMyList() : ''}
              </section>
              <div>
                {!outOfStock && <GenericCardCtaBottomSection>
                  {this.mobileView && <div class='product-card__cta-bottom-section'>
                    {outOfStock ? '' : this.addToMyList()}
                    {isCompare ? (
                      <uiaas-checkbox
                        labelProvider={(e, f) => this.labelProvider(e, f)}
                        isGridLike={this.gridView || this.isDashboardView}
                        skuId={skuId}
                        brand="se"
                        country={this.country}
                        language={this.language}
                        index={this.index}
                        isDashboardView={this.isDashboardView}
                      />
                    ) : ''}
                  </div>}
                  {this.isUnifiedCheckout && this.doc?.isCTO == 'false' && <qds-button
                    onClick={this.addToCardClickHandler}
                    importance='standard'
                    size='standard'
                    icon-name='shopping-cart'
                    icon-library="default"
                    text={addToMyCartText}
                    data-card-area={ucOptions.ADD_TO_MY_CART}
                    href={'javascript: void(0)'}
                    class='product-card__add-to-my-cart'
                  />}
                  {isViewDetailsBtn && <qds-button
                    href={this.doc?.url}
                    importance='standard'
                    size='standard'
                    text={this.labelProvider('product.card.viewDetails', 'View Details')}
                    data-card-area={'view_details'}
                    class='product-card-view-details'
                  />}
                  <ProduktnetSection
                    produktnetUrl={this.doc?.produktnetUrl}
                    labelProvider={(e, f) => this.labelProvider(e, f)}
                  />
                  <ECommerceSection
                    isEcommerceMode={this.eShop}
                    labelProvider={(e, f) => this.labelProvider(e, f)}
                    eCommerceData={eCommerceData as EcommerceSe}
                    isGridView={this.gridView}
                    isDashboardView={this.isDashboardView}
                  />

                  {((sku || this.rangesTerm) && (brandStrategy != 'myschneider') && (!isPromotedWidget)) ? <uiaas-search-product-dropdown-button
                    toggleDropdown={this.toggleDropdown}
                    labelProvider={(e, f) => this.labelProvider(e, f)}
                    isDropdownVisible={this._isVisible}
                    brand={this.brand}
                    language={this.language}
                    country={this.country}
                    documentsTerm={sku}
                    gridView={this.gridView}
                    dashboardView={this.isDashboardView}
                    rangesTerm={this.rangesTerm}
                    isRtl={this.rtl}
                  /> : ''}

                </GenericCardCtaBottomSection> }
              </div>
            </div>
            <GenericCardDropdownBody
              dropdownTrigger={this.toggleDropdown}
              isMobile={this.mobileView}
              isOutOfCard={this._isOutOfCard}
              isVisible={this._isVisible}
              labelProvider={(e, f) => this.labelProvider(e, f)}
              dropdownTitle={dropdownHeader}>
              <ProductDropdownBodyContent
                documents={this.productDocuments}
                ranges={this.productRanges as Array<TransformedRange>}
                isVisible={this._isVisible}
                labelProvider={(e, f) => this.labelProvider(e, f)}
                isGridView={this.gridView}
              />
            </GenericCardDropdownBody>
          </ProductCardContainer>
        </GenericCardEventsLayer>
      );
  }
}
